/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Flex,
    IconButton,

    // Hooks
    useMultiStyleConfig
} from '@chakra-ui/react'

import {
    SocialFacebookIcon,
    SocialInstagramIcon,
    SocialPinterestIcon,
    SocialTikTokIcon,
    SocialTwitterIcon,
    SocialWeChatIcon,
    SocialWeiboIcon,
    SocialXiaohongshuIcon,
    SocialYoutubeIcon
} from '../../components/icons'
import styles from '../../theme/foundations/styles'

export const SocialIcons = ({
    variant,
    socialIcons,
    pinterestInnerColor = 'white',
    ...otherProps
}) => {
    const styles = useMultiStyleConfig('SocialIcons', {variant})

    return (
        <Flex
            className="sf-social-icons"
            {...styles.container}
            sx={{'--pinterest-icon-inner': pinterestInnerColor}}
            {...otherProps}
            marginTop="2.3px"
        >
            {socialIcons?.map(({url, plattform, name}) => {
                const Icon = MapIcon(plattform)
                return (
                    Icon && (
                        <IconButton
                            {...styles.item}
                            key={url}
                            icon={Icon}
                            variant="unstyled"
                            onClick={() => {
                                window.open(url, '_self')
                            }}
                            aria-label={name}
                        />
                    )
                )
            })}
        </Flex>
    )
}

function MapIcon(iconName) {
    const Icons = {
        facebook: <SocialFacebookIcon key={iconName} {...styles.icon}></SocialFacebookIcon>,
        instagram: <SocialInstagramIcon key={iconName} {...styles.icon}></SocialInstagramIcon>,
        pinterest: <SocialPinterestIcon key={iconName} {...styles.icon}></SocialPinterestIcon>,
        twitter: <SocialTwitterIcon key={iconName} {...styles.icon}></SocialTwitterIcon>,
        youtube: <SocialYoutubeIcon key={iconName} {...styles.icon}></SocialYoutubeIcon>,
        weibo: <SocialWeiboIcon key={iconName} {...styles.icon}></SocialWeiboIcon>,
        wechat: <SocialWeChatIcon key={iconName} {...styles.icon}></SocialWeChatIcon>,
        tiktok: <SocialTikTokIcon key={iconName} {...styles.icon}></SocialTikTokIcon>,
        xiaohongshu: <SocialXiaohongshuIcon key={iconName} {...styles.icon}></SocialXiaohongshuIcon>
    }
    return Icons[iconName]
}

SocialIcons.displayName = 'SocialIcons'

SocialIcons.propTypes = {
    /**
     * This component has 3 variants, 'flex', 'flex-start' and 'flex-end'.
     * All will affect how the child icons are displayed. By default, it's
     * value is `flex`.
     */
    variant: PropTypes.string,
    /**
     * The array of social-icons to display. Contains the href, icon and ariaLabel of the SocialIcon to render.
     */
    socialIcons: PropTypes.array,
    /**
     * The inverse color of Pinterest icon's `currentColor`. For example, if the pinterest icon is white, then its inner 'p' is black.
     */
    pinterestInnerColor: PropTypes.string
}

export default SocialIcons
